import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import { ArrowLeft, Edit2, KeyRound, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatAadhar, formatHrsMins } from '../utils/format';

// Convert "HH:MM" (24h) to "h:MM AM/PM"
const to12 = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Minutes the outlet opened late vs its configured opening time, beyond the
// 10-minute grace period — null if not late (or no configured/actual time).
const GRACE_MINUTES = 10;
const openingDelayMinutes = (actualOpen, configuredOpen) => {
  if (!actualOpen || !configuredOpen) return null;
  const [ah, am] = actualOpen.split(':').map(Number);
  const [ch, cm] = configuredOpen.split(':').map(Number);
  const diff = (ah * 60 + am) - (ch * 60 + cm);
  return diff > GRACE_MINUTES ? diff : null;
};

// Minutes the outlet closed early vs its configured closing time, beyond the
// 10-minute grace period — null if not early (or no configured/actual time).
const earlyClosingMinutes = (actualClose, configuredClose) => {
  if (!actualClose || !configuredClose) return null;
  const [ah, am] = actualClose.split(':').map(Number);
  const [ch, cm] = configuredClose.split(':').map(Number);
  let diff = (ch * 60 + cm) - (ah * 60 + am);
  if (diff > 12 * 60) diff -= 24 * 60; // actual time past midnight = closed late, not early
  return diff > GRACE_MINUTES ? diff : null;
};

export default function OutletDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole('super_admin', 'outlet_owner', 'organization');
  const canDelete = hasRole('super_admin');

  const [outlet, setOutlet] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [credInfo, setCredInfo] = useState(null);
  const [dailyTimings, setDailyTimings] = useState([]);

  const fetchOutlet = () => {
    api.get(`/outlets/${id}`)
      .then((r) => setOutlet(r.data))
      .catch(() => setNotFound(true));
  };

  const fetchDailyTimings = () => {
    api.get(`/outlets/${id}/daily-timings`)
      .then((r) => setDailyTimings(r.data))
      .catch(() => setDailyTimings([]));
  };

  useEffect(() => { fetchOutlet(); fetchDailyTimings(); }, [id]);

  const outletToForm = (o) => ({
    name: o.name, address: o.address, email: o.email || '',
    phone: o.phone, organization: o.organization?._id || '',
    gstNo: o.gstNo || '', aadharCard: formatAadhar(o.aadharCard) || '',
    openTime: o.openTime || '', closeTime: o.closeTime || '',
  });

  const startEdit = () => {
    setForm(outletToForm(outlet));
    setEditMode(true);
  };

  const isDirty = () =>
    editMode && JSON.stringify(form) !== JSON.stringify(outletToForm(outlet));

  const attemptCancel = () => {
    if (isDirty()) {
      const save = window.confirm('You have unsaved changes. Do you want to save them?\n\nOK = Save   |   Cancel = Discard');
      if (save) { saveEdit(); return; }
    }
    setEditMode(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/outlets/${id}`, form);
      toast.success('Outlet updated');
      setOutlet(res.data);
      setEditMode(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating');
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = async () => {
    try {
      const res = await api.get(`/outlets/${id}/password`);
      await navigator.clipboard?.writeText(res.data.password);
      toast.success('Password copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not copy password');
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset the login password for "${outlet.name}"?\n\nA new password will be generated and the old one will stop working.`)) return;
    try {
      const res = await api.post(`/outlets/${id}/reset-password`);
      if (res.data.emailSent) {
        toast.success('New password emailed to the outlet.');
      } else {
        setCredInfo({ email: res.data.email, password: res.data.password, note: res.data.note });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error resetting password');
    }
  };

  // ----- Delete -----
  const downloadOutletData = async () => {
    const { data } = await api.get(`/outlets/${id}/export-data`);
    const { staff, attendance } = data;
    const todayStr = new Date().toISOString().slice(0, 10);

    const doc = new jsPDF();
    const navy = [13, 59, 102];
    const white = [255, 255, 255];
    const pageH = 287;
    let y = 20;

    const ensureSpace = (need = 8) => {
      if (y + need > pageH) { doc.addPage(); y = 20; }
    };
    const heading = (text) => {
      ensureSpace(12);
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...navy);
      doc.text(text, 14, y);
      y += 7;
      doc.setDrawColor(228, 237, 246); doc.setLineWidth(0.5);
      doc.line(14, y - 4, 196, y - 4);
    };
    const line = (label, value) => {
      ensureSpace(7);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(String(label), 16, y);
      doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold');
      doc.text(String(value), 90, y);
      y += 7;
    };

    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CloudStay', 14, 12);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Outlet Data Export', 14, 21);
    doc.text(`Generated: ${todayStr}`, 196, 21, { align: 'right' });
    y = 38;

    heading('Outlet Profile');
    line('Outlet ID', outlet.outletId);
    line('Outlet Name', outlet.name);
    line('Organization', outlet.organization?.name || '—');
    line('GST Number', outlet.gstNo || '—');
    line('Aadhar Card', formatAadhar(outlet.aadharCard) || '—');
    line('Mobile', outlet.phone ? `+91 ${outlet.phone}` : '—');
    line('Email', outlet.email || '—');
    line('Address', outlet.address || '—');
    line('Status', outlet.isActive === false ? 'Inactive' : 'Active');
    y += 4;

    heading(`Staff (${staff.length})`);
    if (staff.length) {
      staff.forEach((s) => {
        line(s.staffId, `${s.name} — Rs ${s.salary?.toLocaleString()}`);
      });
    } else {
      line('—', 'No staff');
    }
    y += 4;

    heading(`Attendance Records (${attendance.length})`);
    if (attendance.length) {
      ensureSpace(7);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text('Date', 16, y);
      doc.text('Staff ID', 50, y);
      doc.text('Time In', 80, y);
      doc.text('Time Out', 110, y);
      doc.text('Hours', 140, y);
      doc.text('Status', 165, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      const staffById = Object.fromEntries(staff.map((s) => [s._id, s.staffId]));
      attendance.forEach((a) => {
        ensureSpace(6);
        doc.text(a.date?.split('T')[0] || '—', 16, y);
        doc.text(staffById[a.staff] || '—', 50, y);
        doc.text(to12(a.timeIn) || '—', 80, y);
        doc.text(to12(a.timeOut) || '—', 110, y);
        doc.text(String(a.hoursWorked || 0), 140, y);
        doc.text(a.status || '—', 165, y);
        y += 6;
      });
    } else {
      line('—', 'No attendance records');
    }

    doc.save(`outlet_data_${outlet.outletId}_${outlet.name.replace(/\s+/g, '_')}.pdf`);
  };

  const handleDeleteOutlet = async () => {
    if (!window.confirm(`Delete "${outlet.name}" (${outlet.outletId})?\n\nThis will permanently delete ALL its staff and attendance records. This action cannot be undone.`)) return;
    if (!window.confirm('Download this outlet\'s data before deleting?\n\nOK = Download data & Delete   |   Cancel = Cancel')) return;
    try {
      await downloadOutletData();
      await api.delete(`/outlets/${id}`);
      toast.success('Outlet deleted');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting outlet');
    }
  };

  if (notFound) {
    return (
      <div className="page">
        <div className="empty-state">
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Could not load outlet</div>
          <button className="btn btn-outline" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    );
  }

  if (!outlet) {
    return <div className="page"><div className="loading-screen">Loading...</div></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <button className="back-arrow-btn" onClick={() => (editMode ? attemptCancel() : navigate(-1))} title="Back">
            <ArrowLeft size={22} />
          </button>
          <div>
            <h2 style={{ fontSize: 20 }}>{outlet.name}</h2>
            <p className="text-muted">{outlet.outletId} · {outlet.organization?.name}</p>
          </div>
        </div>
        {canEdit && !editMode && (
          <div className="header-actions">
            <button className="btn btn-outline" onClick={copyPassword} title="Copy the current login password">
              <Copy size={15} /> Copy Password
            </button>
            <button className="btn btn-outline" onClick={resetPassword} title="Generate a new login password">
              <KeyRound size={15} /> Reset Password
            </button>
            <button className="btn btn-primary" onClick={startEdit}>
              <Edit2 size={15} /> Edit
            </button>
          </div>
        )}
      </div>

      <div className="card">
        {!editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: '70% 30%', gap: 24, alignItems: 'start' }}>
            <div>
              <h3 style={{ margin: '0 0 8px' }}>
                Outlet Timings{outlet.openTime && outlet.closeTime ? ` - ${to12(outlet.openTime)} - ${to12(outlet.closeTime)}` : ''}
              </h3>

              <div className="table-container" style={{ marginTop: 16 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Opening Time</th>
                      <th>Closing Time</th>
                      <th>Delay</th>
                      <th>Early</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyTimings.map((d) => {
                      const delay = openingDelayMinutes(d.openTime, outlet.openTime);
                      const early = earlyClosingMinutes(d.closeTime, outlet.closeTime);
                      return (
                        <tr key={d.date}>
                          <td>{new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td>{to12(d.openTime) || '—'}</td>
                          <td>{to12(d.closeTime) || '—'}</td>
                          <td>{delay !== null ? formatHrsMins(delay / 60) : '—'}</td>
                          <td>{early !== null ? formatHrsMins(early / 60) : '—'}</td>
                        </tr>
                      );
                    })}
                    {dailyTimings.length === 0 && (
                      <tr><td colSpan={5} className="empty-state">No attendance-based timing data yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 style={{ margin: '0 0 8px' }}>Outlet Details</h3>
              <div className="staff-detail-view">
                {[
                  ['Outlet ID', outlet.outletId],
                  ['Outlet Name', outlet.name],
                  ['Organization', outlet.organization?.name],
                  ['GST Number', outlet.gstNo],
                  ['Aadhar Card', formatAadhar(outlet.aadharCard)],
                  ['Mobile', outlet.phone ? `+91 ${outlet.phone}` : ''],
                  ['Email', outlet.email],
                  ['Address', outlet.address],
                  ['Status', outlet.isActive === false ? 'Inactive' : 'Active'],
                  ['Created On', outlet.createdAt && new Date(outlet.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="detail-row">
                    <span className="detail-label">{label}</span>
                    <span className="detail-value">{value}</span>
                  </div>
                ))}
                {canDelete && (
                  <div className="detail-row">
                    <span className="detail-label">Delete outlet</span>
                    <span className="detail-value">
                      <a href="#delete" className="link-danger" onClick={(e) => { e.preventDefault(); handleDeleteOutlet(); }}>
                        Delete
                      </a>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); saveEdit(); }} className="form-grid">
            <div className="form-group">
              <label>Outlet ID</label>
              <input value={outlet.outletId} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Organization</label>
              <input value={outlet.organization?.name || '—'} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Outlet Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Email <span className="text-muted">(login — cannot be changed)</span></label>
              <input type="email" value={form.email} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>GST Number</label>
              <input value={form.gstNo} onChange={(e) => setForm({ ...form, gstNo: e.target.value.toUpperCase().slice(0, 15) })} maxLength={15} />
            </div>
            <div className="form-group">
              <label>Aadhar Card</label>
              <input value={form.aadharCard} onChange={(e) => setForm({ ...form, aadharCard: e.target.value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1-') })} maxLength={14} inputMode="numeric" placeholder="0000-0000-0000" />
            </div>
            <div className="form-group">
              <label>Mobile Number *</label>
              <div className="input-group">
                <span className="input-prefix">+91</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} maxLength={10} inputMode="numeric" required />
              </div>
            </div>
            <div className="form-group">
              <label>Outlet Opening Time</label>
              <input type="time" value={form.openTime} onChange={(e) => setForm({ ...form, openTime: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Outlet Closing Time</label>
              <input type="time" value={form.closeTime} onChange={(e) => setForm({ ...form, closeTime: e.target.value })} />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Address *</label>
              <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} required />
            </div>
            <div className="modal-actions" style={{ gridColumn: '1 / -1' }}>
              <button type="button" className="btn btn-outline" onClick={attemptCancel}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Credentials fallback modal (when email could not be sent) */}
      {credInfo && (
        <div className="modal-overlay" onClick={() => setCredInfo(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Outlet Login Updated</h3>
            <p className="info-banner">{credInfo.note}</p>
            <div className="staff-detail-view">
              <div className="detail-row">
                <span className="detail-label">Login Email</span>
                <span className="detail-value">{credInfo.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Password</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{credInfo.password}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => { navigator.clipboard?.writeText(`Email: ${credInfo.email}\nPassword: ${credInfo.password}`); toast.success('Copied'); }}
              >
                Copy
              </button>
              <button className="btn btn-primary" onClick={() => setCredInfo(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
